import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../images/logo/logo.png';
import { useUserStore } from '../../stores/userStore';
import { useNotifications } from '../../contexts/NotificationContext';

const ForgotPassword: React.FC = () => {
  const { requestPasswordReset, isLoading } = useUserStore();
  const { success: showSuccess, errorNotification: showError } = useNotifications();
  
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  
  // Dark mode handling. useColorMode (the hook the rest of the app uses)
  // stores this JSON-encoded (i.e. '"dark"', with quotes) via
  // useLocalStorage — a plain string comparison never matched that.
  useEffect(() => {
    const savedTheme = localStorage.getItem('color-theme');
    const isDark = savedTheme === '"dark"' || savedTheme === 'dark';
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      showError('Please enter your email address', 'Validation Error');
      return;
    }

    try {
      const result = await requestPasswordReset(email);
      if (result) {
        setSubmitted(true);
        showSuccess('If your email exists in our system, you will receive password reset instructions shortly.', 'Request Submitted');
      }
    } catch (err) {
      console.error('Error requesting password reset:', err);
      // We don't show specific errors for security reasons
    }
  };

  return (
    <div className='flex justify-center border items-center h-screen w-screen'>
      <div className="min-h-screen bg-white dark:bg-boxdark-2">
        <div className="flex flex-wrap">
          {/* Left side with logo and background */}
          <div className="hidden xl:block xl:w-1/2">
            <div className="relative h-screen w-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-green-800 opacity-90"></div>
              <div className="relative flex h-full items-center justify-center p-10">
                <div className="text-center">
                  <img 
                    src={Logo} 
                    alt="Re:Loam" 
                    className="mx-auto mb-8 h-32 w-auto opacity-90" 
                  />
                  <h1 className="mb-4 text-4xl font-bold text-white">Welcome to Re:Loam</h1>
                  <p className="text-xl text-green-100">Your land management solution</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side with form */}
          <div className="w-full xl:w-1/2">
            <div className="flex h-screen flex-col items-center justify-center p-4 sm:p-12.5 xl:p-17.5">
              <div className="mb-6 flex justify-center">
                <img 
                  src={Logo} 
                  alt="Re:L Logo" 
                  className="h-16 w-auto dark:invert" 
                />
              </div>
              
              <h2 className="mb-4 text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
                Forgot Password
              </h2>
              
              {!submitted ? (
                <>
                  <p className="mb-9 text-gray-600 dark:text-gray-300">Enter your email to receive password reset instructions</p>
                  
                  <form onSubmit={handleSubmit} className="w-full max-w-md">
                    <div className="mb-6">
                      <label className="mb-2.5 block font-medium text-black dark:text-white">
                        Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                        />
                        <span className="absolute right-4 top-4">
                          <svg
                            className="fill-current"
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g opacity="0.5">
                              <path
                                d="M19.2516 3.30005H2.75156C1.58281 3.30005 0.585938 4.26255 0.585938 5.46567V16.6032C0.585938 17.7719 1.54844 18.7688 2.75156 18.7688H19.2516C20.4203 18.7688 21.4172 17.8063 21.4172 16.6032V5.4313C21.4172 4.26255 20.4203 3.30005 19.2516 3.30005ZM19.2516 4.84692C19.2859 4.84692 19.3203 4.84692 19.3547 4.84692L11.0016 10.2094L2.64844 4.84692C2.68281 4.84692 2.71719 4.84692 2.75156 4.84692H19.2516ZM19.2516 17.1532H2.75156C2.40781 17.1532 2.13281 16.8782 2.13281 16.5344V6.35942L10.1766 11.5157C10.4172 11.6875 10.6922 11.7563 10.9672 11.7563C11.2422 11.7563 11.5172 11.6875 11.7578 11.5157L19.8016 6.35942V16.5688C19.8703 16.9125 19.5953 17.1532 19.2516 17.1532Z"
                                fill=""
                              />
                            </g>
                          </svg>
                        </span>
                      </div>
                    </div>
                    
                    <div className="mb-5">
                      <input
                        type="submit"
                        value={isLoading ? "Sending..." : "Send Reset Instructions"}
                        disabled={isLoading}
                        className={`w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition ${
                          isLoading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-opacity-90'
                        }`}
                      />
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center max-w-md">
                  <div className="mb-6 flex justify-center">
                    <div className="rounded-full bg-green-100 p-4">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-12 w-12 text-green-600" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M5 13l4 4L19 7" 
                        />
                      </svg>
                    </div>
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-black dark:text-white">
                    Check Your Email
                  </h3>
                  <p className="mb-6 text-gray-600 dark:text-gray-300">
                    If your email address exists in our database, you will receive a password recovery link at your email address shortly.
                  </p>
                </div>
              )}
              
              <div className="mt-6 text-center">
                <p>
                  Remember your password?{' '}
                  <Link to="/auth/signin" className="inline-flex min-h-[40px] items-center text-primary">
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
